/**
 * AI Service — Frontend client for the AI Model Integration endpoints
 *
 * Calls the backend proxy at /api/v1/ai/* which handles auth, caching,
 * circuit breaking, and demo mode fallback transparently.
 */

import { apiClient } from './apiClient'

export interface AIScoreResponse {
  data: {
    score: number
    risk_tier: 'low' | 'medium' | 'high' | 'critical'
    confidence_interval: { lower: number; upper: number }
    feature_importance: Record<string, number>
    explanation: string
  } | null
  source: 'ai_api' | 'ai_model' | 'cached' | 'demo' | 'cached_fallback' | 'pending'
  message?: string
  timestamp: string
}

export interface AIAnomalyResponse {
  data: {
    is_anomaly: boolean
    anomaly_score: number
    anomaly_type: string
    severity: 'low' | 'medium' | 'high' | 'critical'
    contributing_features: Array<{ feature: string; contribution: number }>
  }
  source: string
  timestamp: string
}

export interface AIDefaultRiskResponse {
  data: {
    default_probability: number
    is_high_risk: boolean
    risk_factors: Array<{ factor: string; impact: number; description: string }>
    confidence: number
  }
  source: string
  timestamp: string
}

export const aiService = {
  /**
   * Request AI-generated business health score.
   * The backend handles all complexity (auth with AI API, caching, demo fallback).
   */
  async getScore(businessId: string, fresh?: boolean): Promise<AIScoreResponse> {
    const url = fresh ? '/ai/score?fresh=true' : '/ai/score'
    const response = await apiClient.post<AIScoreResponse>(url, {
      business_id: businessId,
    })
    return response.data
  },

  /**
   * Request anomaly detection for a business.
   */
  async getAnomaly(businessId: string): Promise<AIAnomalyResponse> {
    const response = await apiClient.post<AIAnomalyResponse>('/ai/anomaly', {
      business_id: businessId,
    })
    return response.data
  },

  /**
   * Request default risk prediction for a business.
   */
  async getDefaultRisk(businessId: string, horizonDays: 30 | 60 | 90 = 30): Promise<AIDefaultRiskResponse> {
    const response = await apiClient.post<AIDefaultRiskResponse>('/ai/default-risk', {
      business_id: businessId,
      horizon_days: horizonDays,
    })
    return response.data
  },
}
